// Display helpers for ski scores on the summary surfaces: boards, podiums, the 7-day
// forecast strip and the hourly timeline (ski scoring v3, consumer inventory 5.2 and 5.7).
//
// - Labels come from lib/conditions/ski-labels-v3.ts: one vocabulary per sport, "Closed",
//   "No lift access" and "Score unavailable". The platform badge vocabulary stays in use for
//   colour only.
// - A withheld score (Closed, or no lift access off touring) never shows a number.
// - Confidence classes are shown as plain words, with the P10 to P90 range as "Likely 62 to 78".
//   Both come from ./ski/ski-v3-words so the boards and the resort page read the same.
// - Rows without v3 fields (revision 2) have no class, status or extent, so every helper here
//   returns null for them and the callers keep today's UI.
//
// Pure and client safe: no I/O, no clock.
import {
  SKI_LABEL_CLOSED,
  SKI_LABEL_NO_LIFT_ACCESS,
  skiLabel10,
  skiStatusWithholds,
  type SkiPublishedStatusReason,
} from "@/lib/conditions/ski-labels-v3";
import type { SkiV3Extent } from "@/lib/conditions/ski-v3-carriers";
import { SKI_CONFIDENCE_WORDS, confidenceClassWord, likelyRangeText } from "./ski/ski-v3-words";

export type SkiDisplaySport = "piste" | "off-piste" | "touring";
export type SkiConfidenceClass = "A" | "B" | "C";

/** v3 fields a summary surface reads (all optional; absent on revision 2 rows). */
export type SkiDisplayV3Fields = Readonly<{
  status?: string | null;
  statusReason?: string | null;
  cls?: SkiConfidenceClass | null;
  /** P10, 0 to 100 integer. */
  lo?: number | null;
  /** P90, 0 to 100 integer. */
  hi?: number | null;
  extent?: SkiV3Extent | null;
}>;

const REASON_WORDS: Readonly<Record<SkiPublishedStatusReason, string>> = {
  "resort-closed": "Resort closed",
  "lifts-closed": "Lifts closed",
  "run-reports": "Runs reported closed",
  season: "Closed for the season",
  "no-lift-access": "Lifts closed, see touring",
};

/** Reason shown under a resort that is closed for the season. */
export const SKI_SEASON_CLOSED_REASON = REASON_WORDS.season;

function isClass(value: unknown): value is SkiConfidenceClass {
  return value === "A" || value === "B" || value === "C";
}

function int100(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.min(100, Math.round(value)))
    : null;
}

/** Ski sport of a leaderboard (category plus the ski tab); null for every other board. */
export function leaderboardSkiSport(category: string, skiSport: string): SkiDisplaySport | null {
  if (category === "touring") return "touring";
  if (category !== "ski") return null;
  if (skiSport === "offpiste") return "off-piste";
  if (skiSport === "touring") return "touring";
  return "piste";
}

/** Ski sport of a podium category; null for every other podium. */
export function podiumSkiSport(category: string): SkiDisplaySport | null {
  if (category === "piste") return "piste";
  if (category === "offpiste") return "off-piste";
  if (category === "touring") return "touring";
  return null;
}

/** True when the fields carry v3 output (a confidence class). Revision 2 rows have none. */
export function hasSkiV3(fields: SkiDisplayV3Fields | null | undefined): boolean {
  return isClass(fields?.cls);
}

/**
 * The label that replaces the score when it is withheld: "Closed", or "No lift access" for
 * piste and off-piste at a closed resort. Touring needs no lifts, so no lift access keeps its
 * score. Null when the score is not withheld (including every revision 2 row).
 */
export function skiWithheldLabel(sport: SkiDisplaySport, fields: SkiDisplayV3Fields | null | undefined): string | null {
  if (!fields?.status) return null;
  if (!skiStatusWithholds(sport, fields.status, fields.statusReason)) return null;
  return skiLabel10(sport, null, fields.status, fields.statusReason);
}

/** True when the fields say the sport's score is withheld (Closed or no lift access). */
export function isSkiWithheld(sport: SkiDisplaySport, fields: SkiDisplayV3Fields | null | undefined): boolean {
  return skiWithheldLabel(sport, fields) !== null;
}

/**
 * Short reason under a withheld score ("Resort closed", "Closed for the season"). No lift
 * access points to touring, which needs no lifts and still judges the natural snow.
 */
export function skiClosedReason(fields: SkiDisplayV3Fields | null | undefined): string | null {
  if (!fields) return null;
  const reason = fields.statusReason;
  if (reason === "no-lift-access" || fields.status === "no_lift_access") return "Lifts closed, see touring";
  if (typeof reason === "string" && Object.hasOwn(REASON_WORDS, reason)) {
    return REASON_WORDS[reason as SkiPublishedStatusReason];
  }
  if (fields.status === "out-of-season") return REASON_WORDS.season;
  return null;
}

/** Plain class words ("Partly checked"); null without a class. */
export function skiClassWord(cls: string | null | undefined): string | null {
  return confidenceClassWord(cls);
}

/**
 * Short marker shown beside a score: nothing for class A (checked against reports), the
 * class word for B, and "Estimated" for C. Null for revision 2 rows.
 */
export function skiClassMarker(cls: string | null | undefined): string | null {
  if (cls === "B") return SKI_CONFIDENCE_WORDS.B;
  if (cls === "C") return "Estimated";
  return null;
}

/**
 * "Likely 62 to 78" from a P10 and P90 on the 0 to 100 scale (integers, as the readers store
 * them); null when either is missing or invalid. Same text as the resort page.
 */
export function skiLikelyRange(lo: number | null | undefined, hi: number | null | undefined): string | null {
  return likelyRangeText(lo, hi);
}

/** Same as skiLikelyRange for a range on the 0 to 10 scale (lean hourly points, one decimal). */
export function skiLikelyRange10(lo10: number | null | undefined, hi10: number | null | undefined): string | null {
  const scale = (v: number | null | undefined) => typeof v === "number" && Number.isFinite(v) ? Math.round(v * 10) : null;
  return likelyRangeText(scale(lo10), scale(hi10));
}

/**
 * Tooltip for a v3 score: the label, the class words and the likely range, as sentences
 * ("Great. Partly checked. Likely 62 to 78."). Null for revision 2 rows.
 */
export function skiScoreTooltip(label: string | null, fields: SkiDisplayV3Fields | null | undefined): string | null {
  if (!hasSkiV3(fields)) return null;
  const parts = [label, skiClassWord(fields?.cls), skiLikelyRange(fields?.lo, fields?.hi)]
    .filter((part): part is string => typeof part === "string" && part.length > 0);
  return parts.length ? `${parts.join(". ")}.` : null;
}

function extentPct(extent: SkiV3Extent | null | undefined): number | null {
  return int100(extent?.openPct);
}

function extentKm(extent: SkiV3Extent | null | undefined): number | null {
  const km = extent?.openKm;
  return typeof km === "number" && Number.isFinite(km) && km >= 0 ? Math.round(km) : null;
}

/** True when the extent factor lowered the piste headline. */
export function skiExtentLimited(extent: SkiV3Extent | null | undefined): boolean {
  const factor = extent?.factor;
  return typeof factor === "number" && Number.isFinite(factor) && factor < 0.995;
}

/**
 * Compact extent note for a board or podium ("40 % open"). Null when the whole area is open,
 * or when nothing is known, so a full resort shows no chip.
 */
export function skiExtentCompact(extent: SkiV3Extent | null | undefined): string | null {
  const pct = extentPct(extent);
  if (pct !== null) return pct < 100 ? `${pct} % open` : null;
  const km = extentKm(extent);
  return km !== null && skiExtentLimited(extent) ? `${km} km open` : null;
}

/**
 * Full extent note for a tooltip: "Limited terrain: 40 % open (55 km)" when the extent
 * lowered the piste score, otherwise "40 % open (55 km)". Marked as estimated when the
 * share comes only from the usual opening for the date.
 */
export function skiExtentNote(extent: SkiV3Extent | null | undefined): string | null {
  const pct = extentPct(extent);
  const km = extentKm(extent);
  if (pct === null && km === null) return null;
  const core = pct !== null
    ? `${pct} % open${km !== null ? ` (${km} km)` : ""}`
    : `${km} km open`;
  const text = skiExtentLimited(extent) ? `Limited terrain: ${core}` : core;
  return extent?.basis === "prior" ? `${text}, estimated` : text;
}

/** Label of a withheld stored hour (lean hourly `st`); null when the hour has a score. */
export function skiHourWithheldLabel(sport: string, st: string | null | undefined): string | null {
  if (st === "closed") return SKI_LABEL_CLOSED;
  if (st === "no_lift_access") return sport === "touring" ? null : SKI_LABEL_NO_LIFT_ACCESS;
  return null;
}

/**
 * Label of a forecast day whose score is withheld, as set by buildMountainForecastDays
 * ("Closed" or "No lift access" with scoreAvailable false). Null otherwise.
 */
export function forecastDayWithheldLabel(day: Readonly<{ label?: string | null; scoreAvailable?: boolean }>): string | null {
  if (day.scoreAvailable !== false) return null;
  return day.label === SKI_LABEL_CLOSED || day.label === SKI_LABEL_NO_LIFT_ACCESS ? day.label : null;
}

/** Entries whose score for the sport is not withheld (the ranked part of a ski board). */
export function openSkiEntries<T extends SkiDisplayV3Fields>(entries: readonly T[], sport: SkiDisplaySport): T[] {
  return entries.filter((entry) => !isSkiWithheld(sport, entry));
}

/**
 * Entries that may lead a ski board's page description: neither withheld nor closed for the
 * season, so a Closed row never supplies the "leads at" number.
 */
export function leadingSkiEntries<T extends SkiDisplayV3Fields & Readonly<{ seasonClosed?: boolean }>>(
  entries: readonly T[],
  sport: SkiDisplaySport,
): T[] {
  return openSkiEntries(entries, sport).filter((entry) => !entry.seasonClosed);
}
